import { Url } from '@rossum/api-client';
import { Engine } from '@rossum/api-client/engines';
import { Button, Stack } from '@rossum/ui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { PageLayoutV2 } from '../../../components/PageLayoutV2/PageLayoutV2';
import { DeleteConfirmationDialog } from '../../../ui/delete-confirmation-dialog/DeleteConfirmationDialog';
import { ConnectedQueueTile } from '../components/ConnectedQueueTile';
import { ConnectQueueDialog } from '../components/ConnectQueueDialog';
import { EngineErrorAlert } from '../components/EngineErrorAlert';
import { ShowMoreButton } from '../components/ShowMoreButton';
import { TilesListSkeleton } from '../components/skeletons/TilesListSkeleton';
import { TilesList } from '../components/TilesList';
import { TilesListEmptyState } from '../components/TilesListEmptyState';
import { EngineQueueSettingsHeader } from '../headers/EngineQueueSettingsHeader';
import { useEngine } from '../hooks/useEngine';
import { useInfiniteQueueStatsForEngine } from '../hooks/useInfiniteQueueStatsForEngine';
import { usePatchEngine } from '../hooks/usePatchEngine';
import { useUnpaginatedEngineFields } from '../hooks/useUnpaginatedEngineFields';
import { ENGINE_ID_KEY } from '../paths';

export const EngineQueueSettingsContent = ({ engine }: { engine: Engine }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [queueToDisconnect, setQueueToDisconnect] = useState<Url | null>(null);

  const { data: engineFieldsData } = useUnpaginatedEngineFields(
    {
      engine: engine.id,
    },
    { enabled: true }
  );

  const {
    data: queueStats,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status: queuesInfoStatus,
  } = useInfiniteQueueStatsForEngine(engine.id);

  const { mutate: patchEngine, isLoading: isPatchingEngine } = usePatchEngine(
    engine.id
  );

  return (
    <PageLayoutV2
      renderHeader={params => (
        <EngineQueueSettingsHeader
          {...params}
          engine={engine}
          isSaving={false}
        />
      )}
    >
      <ConnectQueueDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={queueUrls =>
          patchEngine({
            trainingQueues: engine.trainingQueues.concat(queueUrls),
          })
        }
        isLoading={isPatchingEngine}
      />
      <DeleteConfirmationDialog
        title={intl.formatMessage({
          id: 'features.engines.engineDetail.connectedQueues.disconnectDialog.title',
        })}
        description={intl.formatMessage({
          id: 'features.engines.engineDetail.connectedQueues.disconnectDialog.description',
        })}
        open={!!queueToDisconnect}
        onCancel={() => setQueueToDisconnect(null)}
        onConfirm={() => {
          patchEngine(
            {
              trainingQueues: engine.trainingQueues.filter(
                queue => queue !== queueToDisconnect
              ),
            },
            { onSuccess: () => setQueueToDisconnect(null) }
          );
        }}
        isLoading={isPatchingEngine}
      />
      <Stack sx={{ p: 4, gap: 2 }} useFlexGap>
        <TilesList
          title={intl.formatMessage({
            id: 'features.engines.engineDetail.connectedQueues.title',
          })}
          items={queueStats.results.filter(
            ({ stats }) => !!stats?.predictionQueue
          )}
          renderTile={({ queue, workspace, stats }) => (
            <ConnectedQueueTile
              dataCy="connected-queue"
              key={queue.id}
              queue={queue}
              workspace={workspace}
              connectedFieldsCount={stats?.numberOfUsedEngineFields}
              totalFieldsCount={engineFieldsData?.length}
              isTrainingEnabled={!!stats?.trainingQueue}
            />
          )}
          status={queuesInfoStatus}
          emptyState={
            <TilesListEmptyState
              title={intl.formatMessage({
                id: 'features.engines.engineDetail.connectedQueues.empty.title',
              })}
            />
          }
          buttons={
            hasNextPage
              ? [
                  <ShowMoreButton
                    key="show-more-results"
                    onClick={fetchNextPage}
                    isFetching={isFetching}
                  />,
                ]
              : []
          }
        />
        <TilesList
          title={intl.formatMessage({
            id: 'features.engines.engineDetail.trainingQueues.title',
          })}
          items={queueStats.results.filter(
            ({ stats }) => !!stats?.trainingQueue
          )}
          renderTile={({ queue, workspace, stats }) => (
            <ConnectedQueueTile
              dataCy="training-queue"
              key={queue.id}
              queue={queue}
              workspace={workspace}
              connectedFieldsCount={stats?.numberOfUsedEngineFields}
              totalFieldsCount={engineFieldsData?.length}
              onRemove={url => setQueueToDisconnect(url)}
              isTrainingEnabled={!!stats?.trainingQueue}
            />
          )}
          status={queuesInfoStatus}
          emptyState={
            <Stack>
              <TilesListEmptyState
                title={intl.formatMessage({
                  id: 'features.engines.engineDetail.trainingQueues.empty.title',
                })}
              />
              <Button
                sx={{ alignSelf: 'center' }}
                onClick={() => setOpen(true)}
                variant="contained"
              >
                {intl.formatMessage({
                  id: 'features.engines.engineDetail.connectedQueues.connectButton',
                })}
              </Button>
            </Stack>
          }
          buttons={[
            <Button
              key="connect-queue"
              onClick={() => setOpen(true)}
              variant="contained"
            >
              {intl.formatMessage({
                id: 'features.engines.engineDetail.connectedQueues.connectButton',
              })}
            </Button>,
          ]}
        />
      </Stack>
    </PageLayoutV2>
  );
};

export const EngineQueueSettingsPage = () => {
  const params = useParams<{ [ENGINE_ID_KEY]: string }>();
  const engineIdNumber = parseInt(params[ENGINE_ID_KEY], 10);
  const {
    data: engine,
    isLoading: engineIsLoading,
    isError,
  } = useEngine(engineIdNumber);

  if (isError) {
    return (
      <PageLayoutV2>
        <EngineErrorAlert />
      </PageLayoutV2>
    );
  }

  if (engineIsLoading) {
    return (
      <PageLayoutV2>
        <TilesListSkeleton />
      </PageLayoutV2>
    );
  }

  if (!engine) {
    return null;
  }

  return <EngineQueueSettingsContent engine={engine} />;
};
